import { useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { toIsoDate } from "../../lib/format";

export interface CalendarGridProps {
  /** ISO "YYYY-MM-DD", or "" if nothing selected yet. */
  selectedIso: string;
  onSelect: (iso: string) => void;
  /** Intl locale tag (e.g. "sr-Latn-RS") — used only for the month/year heading, via Intl.DateTimeFormat. */
  intlLocale: string;
  labels: { previousMonth: string; nextMonth: string };
}

function startOfMonthGrid(year: number, month: number): Date {
  // Grid always starts on a Monday.
  const first = new Date(year, month, 1);
  const isoWeekday = (first.getDay() + 6) % 7; // 0 = Monday
  return new Date(year, month, 1 - isoWeekday);
}

/**
 * Hand-rolled month grid — native `Date` math only, no timezone conversion
 * (every day is constructed from local y/m/d fields, converted to ISO via
 * the same `toIsoDate` helper DateField's manual dd.mm.gggg parsing already
 * uses — one date contract, not two, per NORM_FORM_UX_FIX plan §11).
 */
export function CalendarGrid({ selectedIso, onSelect, intlLocale, labels }: CalendarGridProps) {
  const selected = selectedIso ? new Date(`${selectedIso}T00:00:00`) : null;
  const [viewYear, setViewYear] = useState(selected?.getFullYear() ?? new Date().getFullYear());
  const [viewMonth, setViewMonth] = useState(selected?.getMonth() ?? new Date().getMonth());

  const todayIso = toIsoDate(new Date());
  const gridStart = startOfMonthGrid(viewYear, viewMonth);
  const days: Date[] = Array.from({ length: 42 }, (_, i) => {
    const d = new Date(gridStart);
    d.setDate(gridStart.getDate() + i);
    return d;
  });

  const monthLabel = new Intl.DateTimeFormat(intlLocale, { month: "long", year: "numeric" }).format(
    new Date(viewYear, viewMonth, 1),
  );

  function goToPreviousMonth() {
    const d = new Date(viewYear, viewMonth - 1, 1);
    setViewYear(d.getFullYear());
    setViewMonth(d.getMonth());
  }
  function goToNextMonth() {
    const d = new Date(viewYear, viewMonth + 1, 1);
    setViewYear(d.getFullYear());
    setViewMonth(d.getMonth());
  }

  return (
    <div className="nx-calendar">
      <div className="nx-calendar__header">
        <button
          type="button"
          className="nx-calendar__nav"
          onClick={goToPreviousMonth}
          aria-label={labels.previousMonth}
        >
          <ChevronLeft size={16} />
        </button>
        <span className="nx-calendar__month-label">{monthLabel}</span>
        <button type="button" className="nx-calendar__nav" onClick={goToNextMonth} aria-label={labels.nextMonth}>
          <ChevronRight size={16} />
        </button>
      </div>
      <div className="nx-calendar__grid" role="grid">
        {days.map((day) => {
          const iso = toIsoDate(day);
          const inCurrentMonth = day.getMonth() === viewMonth;
          const isSelected = iso === selectedIso;
          const isToday = iso === todayIso;
          return (
            <button
              type="button"
              key={iso}
              role="gridcell"
              className={[
                "nx-calendar__day",
                inCurrentMonth ? "" : "nx-calendar__day--outside",
                isSelected ? "nx-calendar__day--selected" : "",
                isToday ? "nx-calendar__day--today" : "",
              ]
                .filter(Boolean)
                .join(" ")}
              aria-pressed={isSelected}
              onClick={() => onSelect(iso)}
            >
              {day.getDate()}
            </button>
          );
        })}
      </div>
    </div>
  );
}
